<script setup lang="ts">
import { computed, nextTick, onMounted } from "vue";
import { isNothing, toMaybe } from "../lib/maybe";
import {
  formatGoalProgress,
  goalKindLabel,
  goalTarget,
  progressForKind,
  type GoalKind,
} from "../lib/writingGoals";
import { useProjectGoalsStore } from "../stores/useProjectGoalsStore";
import { useProjectSettingsStore } from "../stores/useProjectSettingsStore";
import ZwButton from "./ui/ZwButton.vue";

const emit = defineEmits<{
  close: [];
}>();

const goalsStore = useProjectGoalsStore();
const settings = useProjectSettingsStore();

onMounted(() => {
  void goalsStore.ensureGoalProgress();
});

type GoalRow = {
  kind: GoalKind;
  label: string;
  current: number;
  target: number;
  met: boolean;
  /** 0–1, clamped for the quiet fill bar. */
  ratio: number;
};

const rows = computed((): GoalRow[] => {
  const goals = settings.goals;
  const kinds: GoalKind[] = ["session", "daily", "project"];
  const out: GoalRow[] = [];
  for (const kind of kinds) {
    const target = goalTarget(
      toMaybe(
        kind === "session"
          ? goals.sessionWords
          : kind === "daily"
            ? goals.dailyWords
            : goals.projectWords,
      ),
    );
    if (isNothing(target)) continue;
    const current = progressForKind(kind, goalsStore.goalsProgress);
    const targetWords = target.value;
    out.push({
      kind,
      label: goalKindLabel(kind),
      current,
      target: targetWords,
      met: current >= targetWords,
      ratio: Math.min(1, current / targetWords),
    });
  }
  return out;
});

async function onEditInSettings(): Promise<void> {
  emit("close");
  await nextTick();
  settings.openSettings("chronicle");
}
</script>

<template>
  <div class="flex min-h-0 flex-1 flex-col">
    <div
      v-if="rows.length === 0"
      class="px-3 py-4 font-ui text-[0.8rem] leading-relaxed text-(--text-muted)"
    >
      No goals set yet.
    </div>

    <ul
      v-else
      class="zw-scroll m-0 min-h-0 flex-1 list-none overflow-y-auto p-0"
      aria-label="Goal progress"
    >
      <li
        v-for="row in rows"
        :key="row.kind"
        class="border-b border-(--line) px-3 py-2.5 last:border-b-0"
      >
        <div class="flex items-baseline justify-between gap-2">
          <span class="font-ui text-[0.82rem] font-medium text-(--text-strong)">
            {{ row.label }}
          </span>
          <span
            class="font-ui text-[0.82rem] tabular-nums text-(--text-muted)"
            :class="{ 'text-(--text-strong)': row.met }"
          >
            {{ formatGoalProgress(row.current, row.target) }}
          </span>
        </div>
        <div
          class="mt-1.5 h-1 overflow-hidden rounded-sm border border-(--line) bg-(--bg)"
          role="progressbar"
          :aria-valuenow="Math.min(row.current, row.target)"
          :aria-valuemin="0"
          :aria-valuemax="row.target"
          :aria-label="`${row.label} progress`"
        >
          <div
            class="h-full bg-(--text-muted)"
            :class="{ 'bg-(--text-strong)': row.met }"
            :style="{ width: `${row.ratio * 100}%` }"
          />
        </div>
      </li>
    </ul>

    <div class="shrink-0 border-t border-(--line) px-3 py-2.5">
      <ZwButton size="sm" class="w-full" @click="onEditInSettings">
        Edit in settings
      </ZwButton>
    </div>
  </div>
</template>
